import React from 'react';
import { fmt, fmtChangeFixed, fmtCount, fmtPct, fmtSigned } from '../../utils/formatters';
import {
  CONDITION_SHORT, GROUP_SHORT, OutlookDay, conditionGlyph, dayLabel, lowF, rangeText, signedMoneyRange,
} from '../../utils/weather';
import WeatherGlyph from './WeatherGlyph';
import { NOTE } from './weatherUi';

// What sits behind one day of the strip: the usual day and how it is found,
// each condition's chance and whether a pattern exists, the category shifts,
// and the dollar math in one line. Opens under the strip, never as a modal.

const longDay = (iso: string) =>
  new Date(`${iso}T12:00:00`).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });

function Why({ d }: { d: OutlookDay }) {
  if (d.closed) return <p className="font-sans text-sm text-[#2D2A2E]">Closed. No call for a day the shop is shut.</p>;
  if (d.call === 'holiday') return <p className="font-sans text-sm text-[#2D2A2E]">A holiday. Holidays run their own way, so there is no weather call.</p>;
  if (d.call === 'unknown') return <p className="font-sans text-sm text-[#2D2A2E]">No forecast for this day yet, so no call.</p>;
  return null;
}

export default function DayDetail({ day: d, id = 'weather-day-detail' }: { day: OutlookDay; id?: string }) {
  const moved = d.call === 'slower' || d.call === 'busier';
  const conditions = d.conditions || [];
  const shifts = (d.shifts || []).filter((s) => s.group !== 'store');
  const low = lowF(d);
  const basis = d.usual_basis;
  return (
    <div id={id} role="region" aria-label={`${longDay(d.date)} in detail`} className="mt-3 rounded-[10px] border border-[#E6E2DE] bg-[#FAF9F7] p-4">
      <h3 className="font-serif text-base text-[#2D2A2E]">{longDay(d.date)}</h3>
      <div className="mt-2 space-y-3">
        <Why d={d} />
        {!d.closed && d.usual != null && (
          <p className="font-sans text-sm tabular-nums text-[#2D2A2E]">
            A usual {d.weekday}: {fmt(d.usual)}
            {basis ? `, the median of the last ${fmtCount(basis.weeks)} ${basis.weekday}s.` : '.'}
          </p>
        )}
        {d.weather && (
          <p className={`${NOTE} tabular-nums`}>
            High {fmtCount(d.weather.high_f)}°F{low != null && `, low ${fmtCount(low)}°F`} · <span className="whitespace-nowrap">rush rain {fmtPct(d.weather.rain_am_chance * 100)}</span>
          </p>
        )}
        {conditions.length > 0 && (
          <ul className="space-y-1.5">
            {conditions.map((c) => (
              <li key={c.condition} className="flex items-start gap-2 font-sans text-sm text-[#2D2A2E]">
                <span className="mt-0.5 text-[#4A2D5E]"><WeatherGlyph kind={conditionGlyph(c.condition)} size={16} /></span>
                <span>
                  {CONDITION_SHORT[c.condition] ?? c.condition}:{' '}
                  <span className="whitespace-nowrap tabular-nums">{fmtPct(c.chance * 100)} chance</span>
                  <span className="text-[#6B6670]">, {c.pattern ? 'pattern found' : 'no pattern yet'}</span>
                </span>
              </li>
            ))}
          </ul>
        )}
        {shifts.length > 0 && (
          <div>
            <div className="font-sans text-xs font-medium text-[#6B6670]">By category</div>
            <ul className="mt-1 divide-y divide-[#E6E2DE]">
              {shifts.map((s) => (
                <li key={`${s.condition}-${s.group}`} className="flex justify-between gap-3 py-1.5 font-sans text-sm">
                  <span className="min-w-0 text-[#2D2A2E]">{GROUP_SHORT[s.group] ?? s.group}</span>
                  <span className="shrink-0 whitespace-nowrap tabular-nums text-[#2D2A2E]">{rangeText(Math.min(s.low, s.high), Math.max(s.low, s.high))}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        {moved && d.usual != null && (
          <div className="border-t border-[#E6E2DE] pt-3">
            <p className="font-sans text-sm font-medium tabular-nums text-[#2D2A2E]">
              usual {fmt(d.usual)} × {fmtChangeFixed((d.change ?? 0) * 100)} ≈ <span className="whitespace-nowrap">{fmtSigned(d.dollars ?? d.usual * (d.change ?? 0))}</span>
            </p>
            <p className={`${NOTE} tabular-nums`}>
              Range <span className="whitespace-nowrap">{signedMoneyRange(d.dollars_low ?? 0, d.dollars_high ?? 0)}</span>
            </p>
          </div>
        )}
        {d.call === 'normal' && !d.closed && (
          <p className="font-sans text-sm text-[#2D2A2E]">Nothing in the forecast moves this day.</p>
        )}
      </div>
    </div>
  );
}
